// src/components/sops/SopEditorPage.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
//
// New SOP (full page) and "Edit people & details". Sections: 1. The file (new only — PDF, up to
// 25 MB), 2. Details, 3. People, 4. Show it on jobs automatically. Every field says Required/Optional.
// SAVE (new): createSopUploadSlot → browser upload to the drop box → finishSopUpload → setSopAccess.
//   Two steps, not one: if the people fail to save, the SOP already exists, so the details page opens
//   with "People weren't saved — click Edit people & details".
// SAVE (edit): updateSopDetails (only what changed) → setSopAccess (only people whose ticks changed).
// READS: folders (one each); edit also the SOP (1), its access records (one each) and the linked
// catalog items' names (one each, at most 50). The People grid uses the team list the app already has.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus } from 'lucide-react';
import { Button, FieldLabel, Notice, inputClass } from './SopUi';
import SopPeopleGrid from './SopPeopleGrid';
import SopCatalogLinks from './SopCatalogLinks';
import SopFolderDialog from './SopFolderDialog';
import {
    loadFolders, loadSop, loadAccess, loadCatalogNames, createSopWithFile, updateDetails, saveAccess, friendlyError,
} from './sopsApi';
import {
    LIMITS, DEADLINE_OPTIONS, CONFIRM_OPTIONS, REREAD_OPTIONS, formatBytes, pdfFileProblem, looksLikePdf,
    liveFolderList, peopleRows, accessChanges, detailChanges, detailsProblem,
    SOPS_PATHS, // sopread-7e4c2a93 — back goes to Manage library (/sops is now My SOPs)
} from '../../utils/sopsUi';

const optionValue = (v) => (v === null || v === undefined ? '' : String(v));
const fromOption = (s) => (s === '' ? null : Number(s));
const indent = (depth) => `${'   '.repeat(depth)}${depth > 0 ? '└ ' : ''}`;

function Section({ n, title, optional, children }) {
    return (
        <section className="rounded-xl border bg-white p-4 md:p-5">
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-[#12192D]">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#5F4EC9] text-xs text-white">{n}</span>
                {title}
                {optional && <span className="text-xs font-normal text-gray-500">Optional</span>}
            </h2>
            {children}
        </section>
    );
}

export default function SopEditorPage({ orgId, mode, sopId, users, initialFolderId }) {
    const navigate = useNavigate();
    const isNew = mode === 'new';
    const [status, setStatus] = useState('loading');
    const [loadError, setLoadError] = useState('');
    const [folders, setFolders] = useState([]);
    const [original, setOriginal] = useState(null); // edit: { details, access: Map }
    const [draft, setDraft] = useState({ title: '', folderId: initialFolderId || null, description: '', daysToRead: 7, rereadMonths: null, confirmType: 'tap' });
    const [catalog, setCatalog] = useState([]);
    const [rows, setRows] = useState([]);
    const [file, setFile] = useState(null);
    const [fileState, setFileState] = useState({ problem: null, checked: false });
    const [saving, setSaving] = useState(null); // null | { step, fraction }
    const [error, setError] = useState('');
    const [folderDialog, setFolderDialog] = useState(false);
    // sopweb-bdb7afcd — the fixed Cancel / Save bar covers the bottom of the page; a spacer of the bar's
    // real height (it grows when it wraps or shows an error) plus a little keeps the last section reachable.
    const barRef = useRef(null);
    const [barHeight, setBarHeight] = useState(72);
    useEffect(() => {
        const el = barRef.current;
        if (!el) return undefined;
        const measure = () => setBarHeight(el.offsetHeight || 72);
        measure();
        if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [status]);

    useEffect(() => {
        let gone = false;
        (async () => {
            try {
                const f = await loadFolders(orgId);
                if (gone) return;
                setFolders(f);
                if (isNew) {
                    setRows(peopleRows(users));
                    setStatus('done');
                    return;
                }
                const [sop, recs] = await Promise.all([loadSop(orgId, sopId), loadAccess(orgId, sopId)]);
                if (gone) return;
                if (!sop) { setStatus('missing'); return; }
                const access = new Map(recs.map((r) => [r.uid, r]));
                const details = {
                    title: sop.title || '', folderId: sop.folderId || null, description: sop.description || '',
                    catalogServiceIds: sop.catalogServiceIds || [], daysToRead: sop.daysToRead ?? null, rereadMonths: sop.rereadMonths ?? null, confirmType: sop.confirmType || 'tap',
                };
                setOriginal({ details, access, archived: sop.archived === true });
                setDraft(details);
                setRows(peopleRows(users, access));
                setCatalog(await loadCatalogNames(orgId, details.catalogServiceIds));
                if (!gone) setStatus('done');
            } catch (e) {
                console.warn('sopweb: editor load failed', e);
                if (!gone) { setStatus('error'); setLoadError(friendlyError(e, 'This page couldn’t be loaded. Please try again.')); }
            }
        })();
        return () => { gone = true; };
        // The team list can tick in live; the grid is built once so nobody's ticks are reset mid-edit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, sopId, isNew]);

    const folderList = useMemo(() => liveFolderList(folders), [folders]);
    const folderIsLive = folderList.some((f) => f.id === draft.folderId);
    const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

    const pickFile = async (f) => {
        setFile(f || null);
        setError('');
        if (!f) { setFileState({ problem: null, checked: false }); return; }
        const problem = pdfFileProblem(f);
        if (problem) { setFileState({ problem, checked: true }); return; }
        try {
            const head = new Uint8Array(await f.slice(0, 5).arrayBuffer());
            setFileState({ problem: looksLikePdf(head) ? null : 'This file isn’t a real PDF (it may just be renamed). Save it as a PDF and choose it again.', checked: true });
        } catch (e) {
            setFileState({ problem: 'This file couldn’t be read. Choose it again.', checked: true });
        }
    };

    const fullDraft = { ...draft, folderId: folderIsLive ? draft.folderId : null, catalogServiceIds: catalog.map((c) => c.id) };
    const save = async () => {
        const problem = detailsProblem(fullDraft, { needsFile: isNew, fileProblem: isNew ? (file ? fileState.problem : 'Choose the PDF file.') : null });
        if (problem) { setError(problem); return; }
        setError('');
        const people = accessChanges(rows, original ? original.access : new Map());
        let savedId = sopId;
        try {
            if (isNew) {
                setSaving({ step: 'upload', fraction: 0 });
                const res = await createSopWithFile(file, {
                    title: fullDraft.title.trim(), folderId: fullDraft.folderId, description: fullDraft.description.trim(),
                    catalogServiceIds: fullDraft.catalogServiceIds, daysToRead: fullDraft.daysToRead, rereadMonths: fullDraft.rereadMonths, confirmType: fullDraft.confirmType,
                }, (step, fraction) => setSaving({ step, fraction }));
                savedId = res.sopId;
            } else {
                const changes = detailChanges(fullDraft, original.details);
                if (changes) { setSaving({ step: 'details' }); await updateDetails(sopId, changes); }
            }
        } catch (e) {
            console.warn('sopweb: save failed', e);
            setSaving(null);
            setError(friendlyError(e, 'The SOP couldn’t be saved. Please try again.'));
            return;
        }
        if (people.length) {
            try {
                setSaving({ step: 'people' });
                await saveAccess(savedId, people);
            } catch (e) {
                console.warn('sopweb: people save failed', e);
                navigate(`/sops/${savedId}`, { replace: true, state: { peopleNotSaved: true, reason: friendlyError(e, '') } });
                return;
            }
        }
        navigate(`/sops/${savedId}`, { replace: isNew, state: { saved: isNew ? 'created' : 'updated' } });
    };

    const back = () => navigate(isNew ? SOPS_PATHS.library : `/sops/${sopId}`);
    const busy = !!saving;
    const savingText = !saving ? '' : saving.step === 'upload' ? `Uploading the file… ${Math.round((saving.fraction || 0) * 100)}%`
        : saving.step === 'check' ? 'Checking the file…' : saving.step === 'details' ? 'Saving details…' : 'Saving people…';

    if (status === 'loading') return <p className="p-20 text-center text-gray-500 animate-pulse">Loading…</p>;
    if (status === 'missing') return <div className="p-8 max-w-xl mx-auto"><Notice tone="error">That SOP doesn’t exist.</Notice><Button className="mt-3" onClick={() => navigate(SOPS_PATHS.library)}>Back to SOPs</Button></div>;
    if (status === 'error') return <div className="p-8 max-w-xl mx-auto"><Notice tone="error">{loadError}</Notice><Button className="mt-3" onClick={() => navigate(SOPS_PATHS.library)}>Back to SOPs</Button></div>;
    if (original && original.archived) return <div className="p-8 max-w-xl mx-auto"><Notice tone="warn">This SOP is archived. Restore it first, then edit it.</Notice><Button className="mt-3" onClick={back}>Back to the SOP</Button></div>;

    return (
        <div className="p-4 md:p-6 max-w-3xl mx-auto">
            <button type="button" onClick={back} className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft size={16} /> {isNew ? 'SOPs' : 'Back to the SOP'}</button>
            <h1 className="mb-4 text-2xl font-bold text-[#12192D]">{isNew ? 'New SOP' : 'Edit people & details'}</h1>

            <div className="space-y-4">
                {isNew && (
                    <Section n={1} title="The file">
                        <FieldLabel htmlFor="sop-file" required hint="PDF only, up to 25 MB.">PDF file</FieldLabel>
                        <input id="sop-file" type="file" accept="application/pdf,.pdf" disabled={busy} onChange={(e) => pickFile(e.target.files && e.target.files[0])}
                            className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-[#5F4EC9]/10 file:px-3 file:py-2 file:text-[#5F4EC9] file:font-medium" />
                        {file && fileState.checked && !fileState.problem && (
                            <p className="mt-2 flex items-center gap-2 text-sm text-[#12192D]"><FileText size={16} className="text-[#5F4EC9]" /> {file.name} · {formatBytes(file.size)}</p>
                        )}
                        {fileState.problem && <div className="mt-2"><Notice tone="error">{fileState.problem}</Notice></div>}
                    </Section>
                )}

                <Section n={isNew ? 2 : 1} title="Details">
                    <div className="space-y-4">
                        <div>
                            <FieldLabel htmlFor="sop-title" required>Title</FieldLabel>
                            <input id="sop-title" className={inputClass} value={draft.title} maxLength={LIMITS.TITLE_MAX} disabled={busy} onChange={(e) => set('title')(e.target.value)} />
                        </div>
                        <div>
                            <FieldLabel htmlFor="sop-folder" required>Folder</FieldLabel>
                            <div className="flex gap-2">
                                <select id="sop-folder" className={inputClass} value={folderIsLive ? draft.folderId : ''} disabled={busy} onChange={(e) => set('folderId')(e.target.value || null)}>
                                    <option value="">Choose a folder…</option>
                                    {folderList.map((f) => <option key={f.id} value={f.id}>{indent(f.depth)}{f.name}</option>)}
                                </select>
                                <Button onClick={() => setFolderDialog(true)} disabled={busy} className="whitespace-nowrap"><Plus size={16} /> New folder</Button>
                            </div>
                        </div>
                        <div>
                            <FieldLabel htmlFor="sop-desc" hint="A line or two about what it covers.">Short description</FieldLabel>
                            <textarea id="sop-desc" rows={3} className={inputClass} value={draft.description} maxLength={LIMITS.DESCRIPTION_MAX} disabled={busy} onChange={(e) => set('description')(e.target.value)} />
                        </div>
                    </div>
                </Section>

                <Section n={isNew ? 3 : 2} title="People">
                    <p className="mb-3 text-sm text-gray-600">Choose who can open it and who must read it. Optional — you can add people later.</p>
                    <SopPeopleGrid rows={rows} onChange={setRows} disabled={busy} />
                    {/* sopweb-bdb7afcd — the three boxes line up along the bottom at every width: the columns
                        stretch to the same height, the label area takes the spare room (labels sit just above
                        their box), and the Required tag has its own line so no label wraps it differently.
                        The edit-only deadline note sits under the whole row so it can't push one box down. */}
                    <div className="mt-4 grid gap-4 sm:grid-cols-3">
                        <div className="flex flex-col">
                            <FieldLabel htmlFor="sop-days" required stacked className="flex flex-1 items-end">People who must read it have</FieldLabel>
                            <select id="sop-days" className={inputClass} value={optionValue(draft.daysToRead)} disabled={busy} onChange={(e) => set('daysToRead')(fromOption(e.target.value))}>
                                {DEADLINE_OPTIONS.map((o) => <option key={optionValue(o.value)} value={optionValue(o.value)}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <FieldLabel htmlFor="sop-confirm" required stacked className="flex flex-1 items-end">How people confirm</FieldLabel>
                            <select id="sop-confirm" className={inputClass} value={draft.confirmType} disabled={busy} onChange={(e) => set('confirmType')(e.target.value)}>
                                {CONFIRM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <FieldLabel htmlFor="sop-reread" required stacked className="flex flex-1 items-end">Ask them to read it again</FieldLabel>
                            <select id="sop-reread" className={inputClass} value={optionValue(draft.rereadMonths)} disabled={busy} onChange={(e) => set('rereadMonths')(fromOption(e.target.value))}>
                                {REREAD_OPTIONS.map((o) => <option key={optionValue(o.value)} value={optionValue(o.value)}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {!isNew && <p className="mt-2 text-xs text-gray-500">Deadline: changing it only affects people you add from now on. People already asked to read it keep their deadline.</p>}
                </Section>

                <Section n={isNew ? 4 : 3} title="Show it on jobs automatically" optional>
                    <p className="mb-3 text-sm text-gray-600">Jobs with these catalog items will show this SOP. (Showing SOPs on jobs comes in a later update — your choices are saved now.)</p>
                    <SopCatalogLinks items={catalog} onChange={setCatalog} disabled={busy} />
                </Section>
            </div>
            <div aria-hidden="true" style={{ height: barHeight + 24 }} />

            <div ref={barRef} className="fixed bottom-0 left-0 right-0 md:left-64 z-30 border-t bg-white/95 px-4 py-3 backdrop-blur">
                <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3">
                    <div className="mr-auto min-w-0 text-sm">
                        {error ? <span className="text-red-700" role="alert">{error}</span> : <span className="text-gray-600" aria-live="polite">{savingText}</span>}
                    </div>
                    <Button onClick={back} disabled={busy}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save SOP'}</Button>
                </div>
            </div>

            {folderDialog && (
                <SopFolderDialog mode="new" folder={null} folders={folders} defaultParentId={folderIsLive ? draft.folderId : null}
                    onClose={() => setFolderDialog(false)}
                    onDone={async () => {
                        setFolderDialog(false);
                        try {
                            const before = new Set(folders.map((f) => f.id));
                            const f = await loadFolders(orgId);
                            setFolders(f);
                            const made = f.find((x) => !before.has(x.id));
                            if (made) set('folderId')(made.id);
                        } catch (e) { setError(friendlyError(e, 'The folders couldn’t be reloaded.')); }
                    }} />
            )}
        </div>
    );
}
